import { memo, useCallback } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { MenuItem, ListItemIcon, Skeleton, Typography, Divider } from '@mui/material'
import { ShowcaseCollection } from '@nativewaves/nw-cloud-sdk/showcase'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useCollectionCreateMutation, useCollectionDeleteMutation } from 'hooks/mutations/showcase'

const RowActionsRaw = ({ row, onClose }: RowActionsProps<ShowcaseCollection>) => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: createShowcaseCollection, ...showcaseCollectionCreateMutation } = useCollectionCreateMutation()
  const { mutate: deleteShowcaseCollection } = useCollectionDeleteMutation()

  const handleDuplicateShowcaseCollection = useCallback(() => {
    createShowcaseCollection(
      { showcaseCollectionInsert: row.original },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }, [createShowcaseCollection, onClose, row.original])

  const handleDeleteShowcaseCollection = useCallback(() => {
    onClose()

    deleteShowcaseCollection(
      { collectionId: row.original.id },
      {
        onSuccess: () =>
          enqueueSnackbar(t('domain:Showcase.Collection.nameDelete', { name: row.original.name }), {
            variant: 'success',
          }),
      },
    )
  }, [deleteShowcaseCollection, enqueueSnackbar, onClose, row.original.id, row.original.name, t])

  return (
    <>
      <MenuItem onClick={handleDuplicateShowcaseCollection} disabled={showcaseCollectionCreateMutation.isPending}>
        <ListItemIcon>
          <ControlPointDuplicate />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {showcaseCollectionCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
        </Typography>
      </MenuItem>
      <MenuItemCopyId onSuccess={onClose} id={row.original.id} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemDelete onConfirm={handleDeleteShowcaseCollection} />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
